/**
 * Podrazumevani raspored: pon–sub radni, nedelja slobodna.
 * Izuzeci se čuvaju u worker.availabilityOverrides.
 */

import { resolveTeamMembers } from './workerUtils';

export const AVAILABILITY_TYPES = {
  WORK: 'work',
  ANNUAL_LEAVE: 'annual_leave',
  DAY_OFF: 'day_off',
  SICK_LEAVE: 'sick_leave'
};

export const AVAILABILITY_LABELS = {
  work: 'Radni dan',
  annual_leave: 'Godišnji odmor',
  day_off: 'Slobodni dan',
  sick_leave: 'Bolovanje'
};

export const AVAILABILITY_CYCLE = [
  AVAILABILITY_TYPES.WORK,
  AVAILABILITY_TYPES.ANNUAL_LEAVE,
  AVAILABILITY_TYPES.DAY_OFF,
  AVAILABILITY_TYPES.SICK_LEAVE
];

export const formatDateKey = (date) => {
  if (!date) return '';
  if (typeof date === 'string') {
    const iso = date.match(/^(\d{4}-\d{2}-\d{2})/);
    if (iso) return iso[1];
  }
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Podrazumevani tip dana bez izuzetaka (nedelja = slobodan). */
export const getDefaultDayAvailability = (date) => {
  const d = typeof date === 'string'
    ? (() => {
        const parts = date.split('-').map(Number);
        return new Date(parts[0], parts[1] - 1, parts[2]);
      })()
    : new Date(date);
  if (Number.isNaN(d.getTime())) return AVAILABILITY_TYPES.WORK;
  return d.getDay() === 0 ? AVAILABILITY_TYPES.DAY_OFF : AVAILABILITY_TYPES.WORK;
};

export const overridesToMap = (overrides = []) => {
  const map = new Map();
  (overrides || []).forEach((entry) => {
    const date = formatDateKey(entry?.date);
    const type = entry?.type;
    if (date && AVAILABILITY_CYCLE.includes(type)) {
      map.set(date, type);
    }
  });
  return map;
};

export const mapToOverrides = (map) =>
  Array.from(map.entries()).map(([date, type]) => ({ date, type }));

/** Tip dana za radnika (izuzetak ili podrazumevano). */
export const getWorkerDayAvailability = (worker, date = new Date()) => {
  const key = formatDateKey(date);
  const override = overridesToMap(worker?.availabilityOverrides).get(key);
  if (override) return override;
  return getDefaultDayAvailability(key || date);
};

export const isWorkerInactiveOnDate = (worker, date = new Date()) => {
  if (worker?.active === false) return true;
  const type = getWorkerDayAvailability(worker, date);
  return type !== AVAILABILITY_TYPES.WORK;
};

export const isWorkerAvailableForWork = (worker, date = new Date()) =>
  worker?.active !== false && !isWorkerInactiveOnDate(worker, date);

export const getAvailabilityDayClass = (type) => {
  switch (type) {
    case AVAILABILITY_TYPES.ANNUAL_LEAVE:
      return 'bg-blue-600/80 text-white';
    case AVAILABILITY_TYPES.DAY_OFF:
      return 'bg-slate-500/80 text-white';
    case AVAILABILITY_TYPES.SICK_LEAVE:
      return 'bg-rose-600/80 text-white';
    case AVAILABILITY_TYPES.WORK:
    default:
      return 'bg-emerald-600/70 text-white';
  }
};

/** Sledeći tip pri kliku na dan (vraća se na podrazumevano ako odgovara). */
export const cycleAvailabilityType = (currentType, defaultType) => {
  const idx = AVAILABILITY_CYCLE.indexOf(currentType);
  const next = AVAILABILITY_CYCLE[(idx + 1) % AVAILABILITY_CYCLE.length];
  if (next === defaultType) return null;
  return next;
};

/** Tim je dostupan samo ako su svi članovi dostupni tog dana. */
export const isTeamAvailableForWork = (team, workers = [], date = new Date()) => {
  if (team?.active === false) return false;
  const members = resolveTeamMembers(team, workers);
  if (members.length === 0) return false;
  return members.every((member) => isWorkerAvailableForWork(member, date));
};

export const getTeamAvailabilityDayClass = (available) =>
  available ? 'bg-emerald-600/70 text-white' : 'bg-slate-500/80 text-white';

export const getTeamDayAvailabilityTitle = (team, workers, date) => {
  if (isTeamAvailableForWork(team, workers, date)) {
    return 'Tim aktivan — svi članovi dostupni';
  }
  const members = resolveTeamMembers(team, workers);
  const unavailable = members.filter((member) => !isWorkerAvailableForWork(member, date));
  if (unavailable.length === 0) {
    return team?.active === false ? 'Tim je neaktivan' : 'Tim nema članova';
  }
  return unavailable
    .map((member) => {
      const type = getWorkerDayAvailability(member, date);
      return `${member.firstName} ${member.lastName}: ${AVAILABILITY_LABELS[type] || 'Nedostupan'}`;
    })
    .join(' · ');
};

const getWorkerUnavailableReason = (worker, serviceDate) => {
  if (!serviceDate) return '';
  if (worker?.active === false) return 'Neaktivan';
  const type = getWorkerDayAvailability(worker, serviceDate);
  if (type === AVAILABILITY_TYPES.WORK) return '';
  return AVAILABILITY_LABELS[type] || 'Nedostupan';
};

export const buildWorkerAssignmentOptions = (workers, serviceDate) =>
  (workers || [])
    .filter((worker) => worker?.active !== false)
    .map((worker) => {
      const available = !serviceDate || isWorkerAvailableForWork(worker, serviceDate);
      const reason = getWorkerUnavailableReason(worker, serviceDate);
      return {
        value: worker._id,
        label: reason
          ? `${worker.firstName} ${worker.lastName} (${reason})`
          : `${worker.firstName} ${worker.lastName}`,
        isDisabled: !available
      };
    });

export const buildTeamAssignmentOptions = (teams, workers, serviceDate) =>
  (teams || [])
    .filter((team) => team?.active !== false)
    .map((team) => {
      const available = !serviceDate || isTeamAvailableForWork(team, workers, serviceDate);
      const members = resolveTeamMembers(team, workers);
      const unavailable = members.filter(
        (member) => serviceDate && !isWorkerAvailableForWork(member, serviceDate)
      );
      const reason =
        unavailable.length > 0
          ? `nedostupan: ${unavailable.map((m) => m.firstName).join(', ')}`
          : members.length === 0
            ? 'nema članova'
            : '';
      return {
        value: team._id,
        label: reason ? `${team.name} (${reason})` : team.name,
        isDisabled: !available || members.length === 0
      };
    });

export const isAssignmentAvailableOnDate = (assignedTo, workers, teams, serviceDate) => {
  if (!assignedTo || !serviceDate) return true;
  const value = String(assignedTo);
  if (value.startsWith('worker:')) {
    const workerId = value.replace('worker:', '');
    const worker = (workers || []).find((w) => String(w._id) === workerId);
    return worker ? isWorkerAvailableForWork(worker, serviceDate) : false;
  }
  if (value.startsWith('team:')) {
    const teamId = value.replace('team:', '');
    const team = (teams || []).find((t) => String(t._id) === teamId);
    return team ? isTeamAvailableForWork(team, workers, serviceDate) : false;
  }
  return true;
};
